import React from "react";
import Helmet from "react-helmet";
import { Box, Button, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { Hero } from "./Hero";
import { VideoBlock } from "./VideoBlock";
import { Testimonials } from "./Testimonials";
import { LogoLink } from "../../components/logo/LogoLink";

const CAL_URL = "https://cal.com/lutian/coach15";
const HERO_PORTRAIT = "/lifecoach/hero-portrait.jpg";
const HERO_PORTRAIT_FALLBACK = "/ll-no-bg.png";
const OG_IMAGE = "/lifecoach/banner.png";
const BANNER_IMAGE = "/lifecoach/banner.png";
const DESCRIPTION =
  "I'm a software engineer who's spent 15+ years thinking about processes and about as long in the gym thinking about habits. Some people I work with have a specific thing they want to change. Others just want to think out loud with someone patient. Either way, we start with a call and see if it clicks.";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: "#0a0a0a",
    backgroundImage:
      "radial-gradient(80% 40% at 50% 0%, rgba(0,191,191,0.06), transparent 60%), radial-gradient(60% 40% at 100% 30%, rgba(219,97,162,0.04), transparent 60%)",
    color: "#fafafa",
    overflowX: "hidden",
    position: "relative",
  },
  paragraphWrap: {
    maxWidth: 720,
    margin: "0 auto",
    padding: theme.spacing(4, 3, 2, 3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 2, 1, 2),
    },
  },
  paragraph: {
    fontFamily: "Roboto, sans-serif",
    fontWeight: 300,
    fontSize: "1.15rem",
    lineHeight: 1.65,
    color: "rgba(255,255,255,0.82)",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.05rem",
      lineHeight: 1.55,
    },
  },
  divider: {
    height: 1,
    width: 60,
    background: "rgba(255,255,255,0.15)",
    margin: theme.spacing(6, "auto", 0, "auto"),
  },
  finalCtaSection: {
    maxWidth: 720,
    margin: "0 auto",
    padding: theme.spacing(10, 3, 12, 3),
    textAlign: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(7, 2, 8, 2),
    },
  },
  finalHeadline: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "2rem",
    fontWeight: 300,
    lineHeight: 1.2,
    color: "#fafafa",
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
    },
  },
  finalSub: {
    fontFamily: "Roboto Mono, monospace",
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.55)",
    marginBottom: theme.spacing(4),
    letterSpacing: "0.02em",
  },
  cta: {
    fontFamily: "Roboto Mono, monospace",
    fontSize: "0.95rem",
    fontWeight: 500,
    color: "#111111",
    backgroundColor: "#00bfbf",
    padding: theme.spacing(1.75, 3.5),
    borderRadius: 999,
    textTransform: "none",
    boxShadow:
      "0 12px 34px -8px rgba(0,191,191,0.55), 0 2px 6px -1px rgba(0,0,0,0.4)",
    transition: "transform 0.4s ease, box-shadow 0.4s ease, background-color 0.4s ease",
    "&:hover": {
      backgroundColor: "#00d4d4",
      transform: "translateY(-1px)",
      boxShadow:
        "0 16px 40px -8px rgba(0,191,191,0.7), 0 2px 6px -1px rgba(0,0,0,0.4)",
    },
  },
  ctaIcon: {
    marginLeft: theme.spacing(1),
    fontSize: "1.05rem",
  },
  // Banner knobs — banner.png is 1920×384 with baked-in text, so it looks
  // pixelated at full-bleed. Dial these three to taste.
  bannerWrap: {
    position: "relative",
    width: "100%",
    maxWidth: 1200, // cap so it doesn't upscale on wide monitors (native 1920)
    margin: `${theme.spacing(2)}px auto 0 auto`,
    overflow: "hidden",
    aspectRatio: "1920 / 384",
    minHeight: 160,
    maxHeight: 320,
    [theme.breakpoints.down("sm")]: {
      aspectRatio: "1920 / 500",
      minHeight: 140,
    },
  },
  bannerImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    opacity: 0.55, // ← main transparency knob
    filter: "saturate(1.05) blur(0.4px)", // ← blur softens fuzziness
  },
  bannerVignette: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(80% 60% at 50% 50%, rgba(10,10,10,0) 30%, rgba(10,10,10,0.65)), linear-gradient(to bottom, rgba(10,10,10,0.4), rgba(10,10,10,0) 25%, rgba(10,10,10,0) 75%, rgba(10,10,10,0.6))",
  },
  footer: {
    padding: theme.spacing(4, 3),
    textAlign: "center",
  },
  footerText: {
    fontFamily: "Roboto Mono, monospace",
    fontSize: "0.72rem",
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "0.06em",
  },
}));

const Lifecoach = () => {
  const classes = useStyles();
  const [portraitSrc, setPortraitSrc] = React.useState(HERO_PORTRAIT);
  const year = new Date().getFullYear();

  React.useEffect(() => {
    const img = new Image();
    img.onerror = () => setPortraitSrc(HERO_PORTRAIT_FALLBACK);
    img.src = HERO_PORTRAIT;
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfessionalService",
        "@id": "https://lucianlazar.io/lifecoach#service",
        name: "Lucian Lazar — Life Coaching",
        url: "https://lucianlazar.io/lifecoach",
        description: DESCRIPTION,
        image: `https://lucianlazar.io${OG_IMAGE}`,
        priceRange: "Free intro call",
        areaServed: "Worldwide",
        serviceType: "Life coaching, habit formation, health advice",
        provider: {
          "@type": "Person",
          "@id": "https://lucianlazar.io/#person",
          name: "Lucian Lazar",
          url: "https://lucianlazar.io",
          image: "https://lucianlazar.io/ll-no-bg.png",
          jobTitle: "Life Coach & Software Engineer",
          knowsLanguage: ["English", "Romanian"],
          sameAs: [
            "https://lucianlazar.io",
            "https://x.com/xucian_",
          ],
        },
        potentialAction: {
          "@type": "ReserveAction",
          target: CAL_URL,
          name: "Book a free 15-min intro call",
        },
      },
      {
        "@type": "WebPage",
        "@id": "https://lucianlazar.io/lifecoach#webpage",
        url: "https://lucianlazar.io/lifecoach",
        name: "Lucian Lazar — Life Coach",
        description: DESCRIPTION,
        inLanguage: "en",
        primaryImageOfPage: `https://lucianlazar.io${OG_IMAGE}`,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>Lucian Lazar — Life Coach | Habit-first, not motivation-first</title>
        <meta name="description" content={DESCRIPTION} />
        <meta
          name="keywords"
          content="life coach, life coaching, habit coach, accountability coach, health advice, nutrition, gym habits, work-life balance, Bucharest, Romania, remote coaching, Lucian Lazar"
        />
        <meta name="author" content="Lucian Lazar" />
        <meta name="theme-color" content="#0a0a0a" />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link rel="canonical" href="https://lucianlazar.io/lifecoach" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lucian Lazar" />
        <meta property="og:title" content="Lucian Lazar — Life Coach" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content="https://lucianlazar.io/lifecoach" />
        <meta property="og:image" content={`https://lucianlazar.io${OG_IMAGE}`} />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1920" />
        <meta property="og:image:height" content="384" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Lucian Lazar — Life Coach" />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`https://lucianlazar.io${OG_IMAGE}`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Box className={classes.root}>
        <LogoLink />

        <Hero portraitSrc={portraitSrc} />

        <Box className={classes.paragraphWrap}>
          <Typography component="p" className={classes.paragraph}>
            {DESCRIPTION}
          </Typography>
          <Box className={classes.divider} />
        </Box>

        <VideoBlock />

        <Testimonials />

        <Box className={classes.finalCtaSection}>
          {/* <Typography component="h2" className={classes.finalHeadline}>
            One call. See if it clicks.
          </Typography>
          <Typography className={classes.finalSub}>
            Free, 15 minutes, no follow-up unless you want one.
          </Typography> */}
          <Button
            className={classes.cta}
            href={CAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            disableElevation
          >
            Book a free 15-min intro call
            <ArrowForwardIcon className={classes.ctaIcon} />
          </Button>
        </Box>

        {/* TODO readd this banner maybe */}
        {/* <Box className={classes.bannerWrap}>
          <img
            className={classes.bannerImg}
            src={BANNER_IMAGE}
            alt="Life coaching with Lucian Lazar"
            loading="lazy"
          />
          <Box className={classes.bannerVignette} />
        </Box> */}

        <Box className={classes.footer}>
          <Typography className={classes.footerText}>
            &copy; {year} Lucian Lazar
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default Lifecoach;
